import React from 'react';
import { AlertCircle, FolderOpen, Upload } from 'lucide-react';
import { Button, RobbieMark } from '../../ui';

/**
 * First-run hero, ported from `Dashboard - Empty State.html`: Robbie's
 * greeting and promise on the AI-panel wash, then the drop zone that starts
 * the whole journey. Presentational — the dropzone props come from
 * `useFirstRfqUpload`, so the /design gallery can render it without a backend.
 */
export interface FirstRunHeroProps {
  firstName: string;
  rootProps?: React.HTMLAttributes<HTMLElement>;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  isDragActive?: boolean;
  starting?: boolean;
  error?: string | null;
  onBrowse?: () => void;
}

export const FirstRunHero: React.FC<FirstRunHeroProps> = ({
  firstName,
  rootProps,
  inputProps,
  isDragActive = false,
  starting = false,
  error,
  onBrowse,
}) => (
  <section className="rounded-panel border border-robbie-border bg-ai-panel px-[26px] py-s7">
    <div className="flex flex-wrap items-start gap-s6">
      <RobbieMark size="lg" state="waving" aria-label="Robbie" />
      <div className="min-w-[240px] flex-1">
        <h1 className="m-0 mb-s2 text-[20px] font-bold tracking-[-0.01em] text-ink">
          Hi {firstName} — I'm Robbie. Let's source your first part.
        </h1>
        <p className="m-0 text-[13.5px] leading-[1.6] text-ink-secondary">
          Drop in a Bill of Materials and I'll read it, find suppliers who can actually make it, run the
          outreach, and bring you quotes ready to award. You approve every step.
        </p>
      </div>
    </div>

    <div
      {...rootProps}
      className={`mt-s6 flex flex-wrap items-center gap-[16px] rounded-card border p-s6 transition-colors ${
        isDragActive ? 'border-action bg-action-bg' : 'border-robbie-border bg-surface'
      }`}
    >
      {inputProps && <input {...inputProps} />}
      <span className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-[11px] border border-action-border bg-action-bg">
        <FolderOpen className="h-[21px] w-[21px] text-action" />
      </span>
      <div className="min-w-[200px] flex-1">
        <div className="text-[14px] font-bold text-ink">Drop your BOM here</div>
        <div className="mt-[2px] text-[12px] text-ink-muted">
          Excel, CSV, PDF or a folder of datasheets — I'll sort out the rest
        </div>
      </div>
      <Button size="lg" iconLeft={<Upload />} onClick={onBrowse} disabled={starting} className="shrink-0">
        {starting ? 'Starting…' : 'Browse files'}
      </Button>
    </div>

    {error && (
      <p className="mt-s4 flex items-start gap-s2 text-meta text-danger-ink" role="alert">
        <AlertCircle className="mt-[1px] h-[13px] w-[13px] shrink-0" />
        {error}
      </p>
    )}
  </section>
);

export default FirstRunHero;
